<template>
  <v-dialog :model-value="modelValue" max-width="720" @update:model-value="$emit('update:modelValue', $event)">
    <v-card class="d-flex flex-column" height="80vh" max-height="90vh">
      <v-card-title class="d-flex align-center justify-space-between pa-4 flex-shrink-0">
        <span>MCP Setup Instructions</span>
        <v-btn icon variant="text" @click="$emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="flex-shrink-0" />

      <v-card-text class="pa-4 flex-grow-1 overflow-y-auto">
        <p class="text-body-2 mb-4">
          The Model Context Protocol (MCP) allows AI assistants to access your {{ brand.name }} workflows.
          Choose your client below and follow the setup instructions.
        </p>

        <v-alert
          v-if="!isOnIntegrationsPage"
          density="compact"
          variant="tonal"
          color="primary"
          class="api-key-alert mb-4"
        >
          <div class="api-key-alert-grid">
            <v-icon size="20" color="primary">mdi-information-outline</v-icon>
            <span class="text-body-2">
              You'll need an API key for MCP. Generate one on the Integrations page.
            </span>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              @click="goToIntegrations"
            >
              Open Integrations
            </v-btn>
          </div>
        </v-alert>

        <v-expansion-panels variant="accordion">
          <!-- Claude Code -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-console</v-icon>
                <span class="font-weight-medium">Claude Code (CLI)</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Connect MCP Server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Run this command in your project's folder:
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ claudeCodeCommand }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(claudeCodeCommand)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mt-3">
                  Replace <code>YOUR_API_KEY</code> with your generated API key. This adds {{ brand.name }} to that project
                  only. To use it in every project, add <code class="text-primary font-weight-bold">--scope user</code> to the
                  command.
                </p>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Install the Plugin</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Install the {{ brand.name }} plugin to enhance your AI assistant:
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ claudePluginCommands }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(claudePluginCommands)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mt-3">
                  The plugin doesn't update on its own. To get new versions automatically, run <code>/plugin</code>, open
                  <strong>Marketplaces</strong>, select <strong>qlerify-plugins</strong> and choose <strong>Enable auto-update</strong>.
                </p>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Cursor -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-cursor-default-outline</v-icon>
                <span class="font-weight-medium">Cursor</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Connect MCP Server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Add this configuration to your Cursor MCP settings file
                  (<code>~/.cursor/mcp.json</code>):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ cursorConfig }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(cursorConfig)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mt-3">
                  Replace <code>YOUR_API_KEY</code> with your generated API key.
                </p>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Set up Skills</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Clone the
                  <a :href="pluginsRepoUrl" target="_blank" rel="noopener">qlerify-plugins repository</a>
                  (or use GitHub's <strong>Code → Download ZIP</strong>):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ pluginsCloneCommand }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(pluginsCloneCommand)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>

                <p class="text-body-2 mt-3 mb-2">
                  Copy skill folders from <code>plugins/qlerify/skills/</code> into
                  <strong>one</strong> of these locations:
                </p>
                <ul class="text-body-2 setup-steps">
                  <li>
                    <strong>Workspace skills</strong> — available only in this project.
                    Folder: <code>&lt;your-workspace&gt;/.cursor/skills/</code>
                  </li>
                  <li>
                    <strong>Global skills</strong> — available across every workspace.
                    Folder: <code>~/.cursor/skills/</code>
                  </li>
                </ul>
                <div class="code-block-wrapper mt-2">
                  <pre class="code-block">{{ cursorSkillsLayout }}</pre>
                </div>

                <p class="text-body-2 mt-3">
                  Then reload the window (<code>Cmd+Shift+P</code> →
                  <strong>Developer: Reload Window</strong>) so Cursor picks up the skills.
                </p>

                <p class="text-caption text-medium-emphasis mt-3">
                  See the
                  <a href="https://cursor.com/docs/skills" target="_blank" rel="noopener">official skills documentation</a>
                  for more details.
                </p>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Antigravity IDE -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-rocket-launch-outline</v-icon>
                <span class="font-weight-medium">Antigravity IDE</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Open the raw MCP config</span>
                </div>
                <ol class="text-body-2 setup-steps mt-2">
                  <li>Open the Agent panel on the right side of the IDE</li>
                  <li>
                    Click the
                    <v-chip size="x-small" variant="tonal" color="primary" label class="dots-chip">···</v-chip>
                    button at the top of the panel
                  </li>
                  <li>Select <strong>MCP Servers</strong> from the dropdown</li>
                  <li>
                    Click
                    <strong>Manage MCP Servers</strong>,
                    then <strong>View raw config</strong> (opens <code>~/.gemini/config/mcp_config.json</code>)
                  </li>
                </ol>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Add the {{ brand.name }} server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Paste this into the config (merge into <code>mcpServers</code> if you already have other servers):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ antigravityConfig }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(antigravityConfig)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>

              <p class="text-caption text-medium-emphasis mt-3">
                Replace <code>YOUR_API_KEY</code> with your generated API key. Antigravity IDE uses
                <code>serverUrl</code> (not <code>url</code>) for HTTP servers, and the key must be
                passed via the <code>Authorization: Bearer</code> header (the <code>x-api-key</code>
                header other clients use is not forwarded). Fully quit and reopen Antigravity IDE
                for the change to take effect.
              </p>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">3</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Set up Skills</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Clone the
                  <a :href="pluginsRepoUrl" target="_blank" rel="noopener">qlerify-plugins repository</a>
                  (or use GitHub's <strong>Code → Download ZIP</strong>):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ pluginsCloneCommand }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(pluginsCloneCommand)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>

                <p class="text-body-2 mt-3 mb-2">
                  Copy skill folders from <code>plugins/qlerify/skills/</code> into
                  <strong>one</strong> of these locations:
                </p>
                <ul class="text-body-2 setup-steps">
                  <li>
                    <strong>Workspace skills</strong> — available only in this project.
                    Folder: <code>&lt;your-workspace&gt;/.agents/skills/</code>
                  </li>
                  <li>
                    <strong>Global skills</strong> — available across every workspace.
                    Folder: <code>~/.gemini/config/skills/</code>
                  </li>
                </ul>
                <div class="code-block-wrapper mt-2">
                  <pre class="code-block">{{ antigravitySkillsLayout }}</pre>
                </div>

                <p class="text-caption text-medium-emphasis mt-3">
                  See the
                  <a href="https://antigravity.google/docs/skills" target="_blank" rel="noopener">official skills documentation</a>
                  for more details.
                </p>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Lovable -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-heart-outline</v-icon>
                <span class="font-weight-medium">Lovable</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 mb-3">
                Add {{ brand.name }} as a custom MCP server in Lovable:
              </p>
              <ol class="text-body-2 lovable-steps">
                <li>
                  Open <strong>Connectors</strong>, click the <strong>+</strong> button in the top right and choose
                  <strong>MCP server</strong>
                </li>
                <li>
                  Set <strong>Server name</strong>: <code>{{ brand.name }}</code>
                </li>
                <li>
                  Set <strong>Server URL</strong>:
                  <div class="code-block-wrapper mt-1">
                    <pre class="code-block">{{ mcpServerUrl }}</pre>
                    <v-btn
                      size="x-small"
                      variant="tonal"
                      class="copy-btn"
                      @click="copyToClipboard(mcpServerUrl)"
                    >
                      <v-icon size="14">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </li>
                <li>For authentication, select <strong>Bearer token or API key</strong></li>
                <li>Enter your {{ brand.name }} API key</li>
                <li>Click <strong>Add server</strong></li>
              </ol>
              <p class="text-caption text-medium-emphasis mt-3">
                Custom MCP servers are available on all Lovable plans. On Business and Enterprise plans, workspace admins can
                turn them off under <strong>Connectors</strong> → <strong>Admin settings</strong> → <strong>Chat connectors</strong>.
              </p>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- VS Code -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-microsoft-visual-studio-code</v-icon>
                <span class="font-weight-medium">VS Code</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Connect MCP Server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Add this to your MCP config file (<code>.vscode/mcp.json</code>):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ vscodeConfig }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(vscodeConfig)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mt-3">
                  Replace <code>YOUR_API_KEY</code> with your generated API key.
                </p>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Install the Plugin</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Install the {{ brand.name }} plugin to enhance your AI assistant:
                </p>
                <v-btn
                  size="small"
                  variant="tonal"
                  color="primary"
                  prepend-icon="mdi-open-in-new"
                  :href="vscodeAddMarketplaceUrl"
                >
                  Add to VS Code
                </v-btn>
                <p class="text-caption text-medium-emphasis mt-2">
                  VS Code asks to add the plugin marketplace. Click <strong>Add Marketplace</strong>, then click
                  <strong>Install</strong> on <strong>{{ pluginDisplayName }}</strong> in the Extensions view that opens.
                </p>

                <p class="text-body-2 mt-4 mb-2">
                  Or add the marketplace yourself in your user settings
                  (<strong>Preferences: Open User Settings (JSON)</strong>):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ vscodePluginSettings }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(vscodePluginSettings)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-body-2 mt-3">
                  Then open the Extensions view, search for <code>@agentPlugins</code> and install <strong>{{ pluginDisplayName }}</strong>.
                </p>
                <p class="text-caption text-medium-emphasis mt-3">
                  This setting replaces VS Code's default list, so the snippet also lists VS Code's own marketplaces. The
                  plugin's skills then show up in the chat's <strong>Configure Skills</strong> menu.
                </p>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Gemini CLI -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-google</v-icon>
                <span class="font-weight-medium">Gemini CLI</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-caption text-medium-emphasis mb-4">
                Gemini CLI now only works with a paid Gemini or Gemini Enterprise Agent Platform API key, or a Gemini Code
                Assist Standard or Enterprise license. With a personal Google account, use Antigravity instead.
              </p>

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Connect MCP Server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Run this command in your terminal:
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ geminiCliCommand }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(geminiCliCommand)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mt-3">
                  Replace <code>YOUR_API_KEY</code> with your generated API key.
                </p>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Install Skills</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Install the {{ brand.name }} skills to enhance your AI assistant:
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ geminiSkillsCommand }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(geminiSkillsCommand)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Claude Web & Desktop -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-web</v-icon>
                <span class="font-weight-medium">Claude Web &amp; Desktop</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Connect MCP Server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Add {{ brand.name }} as a custom connector. Sign-in happens via OAuth, so no API key is needed.
                </p>
                <v-btn
                  size="small"
                  variant="tonal"
                  color="primary"
                  prepend-icon="mdi-open-in-new"
                  :href="claudeConnectorInstallUrl"
                  target="_blank"
                  rel="noopener"
                >
                  Add to Claude
                </v-btn>
                <p class="text-caption text-medium-emphasis mt-2">
                  Opens claude.ai with the name and URL filled in. Click <strong>Add</strong>, then sign in with your
                  {{ brand.name }} account.
                </p>

                <p class="text-body-2 mt-4 mb-2">
                  Or add it yourself:
                </p>
                <ol class="text-body-2 setup-steps">
                  <li>
                    In <a href="https://claude.ai" target="_blank" rel="noopener">claude.ai</a> or Claude Desktop, go to
                    <strong>Customize</strong> → <strong>Connectors</strong>
                  </li>
                  <li>Click <strong>Add custom connector</strong></li>
                  <li>
                    Set <strong>Name</strong>: <code>{{ brand.name }}</code>
                  </li>
                  <li>
                    Set <strong>URL</strong>:
                    <div class="code-block-wrapper mt-1">
                      <pre class="code-block">{{ mcpOAuthUrl }}</pre>
                      <v-btn
                        size="x-small"
                        variant="tonal"
                        class="copy-btn"
                        @click="copyToClipboard(mcpOAuthUrl)"
                      >
                        <v-icon size="14">mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                  </li>
                  <li>Click <strong>Add</strong>, then sign in with your {{ brand.name }} account</li>
                </ol>
                <p class="text-caption text-medium-emphasis mt-3">
                  Custom connectors are available on all plans (one on Free). The connector is saved to your Claude account, so
                  it works on the web and in Claude Desktop. On Team and Enterprise plans, an Owner adds it under
                  <strong>Organization settings</strong> → <strong>Connectors</strong>, then each member clicks
                  <strong>Connect</strong> on it.
                </p>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Install the Plugin</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Install the {{ brand.name }} plugin to enhance your AI assistant:
                </p>
                <ol class="text-body-2 setup-steps">
                  <li>
                    Go to <strong>Customize</strong> → <strong>Plugins</strong>, click <strong>Add</strong> and choose
                    <strong>Add marketplace</strong>
                  </li>
                  <li>
                    Choose <strong>Add from a repository</strong>, enter this repository and click <strong>Sync</strong>:
                    <div class="code-block-wrapper mt-1">
                      <pre class="code-block">{{ pluginsMarketplace }}</pre>
                      <v-btn
                        size="x-small"
                        variant="tonal"
                        class="copy-btn"
                        @click="copyToClipboard(pluginsMarketplace)"
                      >
                        <v-icon size="14">mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                  </li>
                  <li>Find the <strong>{{ pluginDisplayName }}</strong> plugin from qlerify-plugins and click <strong>Add</strong></li>
                </ol>
                <p class="text-caption text-medium-emphasis mt-3">
                  Plugins are available on Pro, Max, Team, and Enterprise plans. Skills also need
                  <strong>Code execution and file creation</strong> turned on under <strong>Settings</strong> →
                  <strong>Capabilities</strong>, and on Team and Enterprise plans an Owner has to allow it too. Claude uses a
                  skill when your request matches it, or type <code>/</code> to pick one. The <code>sync</code> and
                  <code>download</code> skills need local file access, so use them in Claude Code rather than in chat.
                </p>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- ChatGPT -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-message-text-outline</v-icon>
                <span class="font-weight-medium">ChatGPT</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 mb-3">
                Add {{ brand.name }} as a custom app. Sign-in happens via OAuth, so no API key is needed.
              </p>
              <ol class="text-body-2 setup-steps">
                <li>
                  In ChatGPT, turn on <strong>Developer mode</strong> under <strong>Settings</strong> →
                  <strong>Security and login</strong>
                </li>
                <li>Go to <strong>Plugins</strong> and click the <strong>+</strong> button to create an MCP app</li>
                <li>
                  Set <strong>Name</strong>: <code>{{ brand.name }}</code> (if ChatGPT says the name already exists, use another
                  one, such as <code>{{ brand.name }} MCP</code>)
                </li>
                <li>
                  Set <strong>Description</strong>: <code>Access your {{ brand.name }} workflows and domain models.</code>
                </li>
                <li>
                  Under <strong>Connection</strong>, set the server URL:
                  <div class="code-block-wrapper mt-1">
                    <pre class="code-block">{{ mcpOAuthUrl }}</pre>
                    <v-btn
                      size="x-small"
                      variant="tonal"
                      class="copy-btn"
                      @click="copyToClipboard(mcpOAuthUrl)"
                    >
                      <v-icon size="14">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </li>
                <li>Set <strong>Authentication</strong>: <strong>OAuth</strong></li>
                <li>Confirm the notice and create the app</li>
                <li>Sign in with your {{ brand.name }} account in the window that opens</li>
              </ol>
              <p class="text-caption text-medium-emphasis mt-3">
                To use it, start a new chat, open the <strong>+</strong> menu, choose <strong>Developer mode</strong> and select
                {{ brand.name }}. Developer mode is available on ChatGPT Plus, Pro, Business, Enterprise, and Edu, on the web. On
                Business, only admins can use Developer mode, so an admin creates the app and publishes it for the workspace. On
                Enterprise and Edu, an admin has to give you Developer mode access first.
              </p>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-divider class="flex-shrink-0" />

      <v-card-actions class="pa-4 flex-shrink-0">
        <v-spacer />
        <v-btn variant="tonal" @click="$emit('update:modelValue', false)">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useBrand } from '@/composables/useBrand'

defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const store = useStore()
const route = useRoute()
const router = useRouter()
const brand = useBrand()

const isOnIntegrationsPage = computed(() => route.path === '/account/integration')

const goToIntegrations = () => {
  emit('update:modelValue', false)
  if (!isOnIntegrationsPage.value) {
    router.push('/account/integration')
  }
}

const mcpServerUrl = process.env.VUE_APP_MCP_URL || 'https://your-mcp-server-url'
const mcpOAuthUrl = `${mcpServerUrl}/mcp`
const pluginsRepo = 'https://github.com/qlerify/qlerify-plugins.git'
const pluginsRepoUrl = 'https://github.com/qlerify/qlerify-plugins'
const pluginsMarketplace = 'qlerify/qlerify-plugins'
const pluginDisplayName = 'Qlerify'
const vscodeAddMarketplaceUrl = `vscode://chat-plugin/add-marketplace?ref=${encodeURIComponent(btoa(pluginsMarketplace))}`
const claudeConnectorInstallUrl = `https://claude.ai/customize/connectors?modal=add-custom-connector&connectorName=${encodeURIComponent(brand.name)}&connectorUrl=${encodeURIComponent(mcpOAuthUrl)}`

const claudeCodeCommand = computed(() => {
  return `claude mcp add --transport http qlerify ${mcpServerUrl} \\
  --header "x-api-key: YOUR_API_KEY"`
})

const claudePluginCommands = computed(() => {
  return [
    `/plugin marketplace add ${pluginsMarketplace}`,
    '/plugin install qlerify@qlerify-plugins'
  ].join('\n')
})

const cursorConfig = computed(() => {
  return JSON.stringify({
    mcpServers: {
      qlerify: {
        url: mcpServerUrl,
        headers: {
          'x-api-key': 'YOUR_API_KEY'
        }
      }
    }
  }, null, 2)
})

const pluginsCloneCommand = computed(() => {
  return `git clone ${pluginsRepo}`
})

const vscodeConfig = computed(() => {
  return JSON.stringify({
    servers: {
      qlerify: {
        type: 'http',
        url: mcpServerUrl,
        headers: {
          'x-api-key': 'YOUR_API_KEY'
        }
      }
    }
  }, null, 2)
})

const vscodePluginSettings = computed(() => {
  return [
    '"chat.plugins.enabled": true,',
    '"chat.plugins.marketplaces": [',
    '  "github/copilot-plugins",',
    '  "github/awesome-copilot#marketplace",',
    `  "${pluginsMarketplace}"`,
    ']'
  ].join('\n')
})

const geminiCliCommand = computed(() => {
  return `gemini mcp add \\
  --scope user \\
  --transport http \\
  --header "x-api-key: YOUR_API_KEY" \\
  qlerify "${mcpServerUrl}"`
})

const geminiSkillsCommand = computed(() => {
  return `gemini skills install ${pluginsRepo} \\
  --path plugins/qlerify/skills`
})

const skillTree = [
  '└── skills/',
  '    ├── workflow-creation/',
  '    │   ├── SKILL.md',
  '    │   ├── references/',
  '    │   └── examples/',
  '    └── code-generation/',
  '        └── SKILL.md'
]

const cursorSkillsLayout = ['.cursor/', ...skillTree].join('\n')
const antigravitySkillsLayout = ['.agents/', ...skillTree].join('\n')

const antigravityConfig = computed(() => {
  return JSON.stringify({
    mcpServers: {
      qlerify: {
        serverUrl: mcpServerUrl,
        headers: {
          Authorization: 'Bearer YOUR_API_KEY',
          'Content-Type': 'application/json'
        }
      }
    }
  }, null, 2)
})

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    store.commit('snackbar/showMessage', {
      content: 'Copied to clipboard!',
      timeout: 2000,
      color: 'success'
    }, { root: true })
  } catch (err) {
    store.commit('snackbar/showMessage', {
      content: 'Failed to copy to clipboard.',
      timeout: 3000,
      color: 'error'
    }, { root: true })
  }
}
</script>

<style scoped>
.api-key-alert :deep(.v-alert__content) {
  padding-block: 4px;
}

.api-key-alert-grid {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 12px;
  row-gap: 8px;
}

@media (max-width: 600px) {
  .api-key-alert-grid {
    grid-template-columns: auto 1fr;
  }
  .api-key-alert-grid > .v-btn {
    grid-column: 1 / -1;
    justify-self: start;
  }
}

.dots-chip {
  vertical-align: baseline;
  margin: 0 4px;
  padding: 0 8px;
  min-height: 20px;
}

.dots-chip :deep(.v-chip__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}

.code-block-wrapper {
  position: relative;
}

.code-block {
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  background-color: #1e1e1e;
  color: #d4d4d4;
  padding: 16px;
  padding-right: 48px;
  border-radius: 6px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  line-height: 1.5;
}

.copy-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 28px !important;
  width: 28px;
  height: 28px;
  padding: 0 !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: #d4d4d4 !important;
}

.copy-btn:hover {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-badge {
  flex-shrink: 0;
}

.lovable-steps,
.setup-steps {
  padding-left: 20px;
  margin-bottom: 0;
}

.lovable-steps li,
.setup-steps li {
  margin-bottom: 8px;
}
</style>
